import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { MapPin, Check, Route } from "lucide-react";

function numberIcon(n, done) {
  const color = done ? "#2F8F6E" : "#FF7A59";
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.25);border:2px solid #fff;display:flex;align-items:center;justify-content:center">
      <span style="transform:rotate(45deg);color:#fff;font:800 13px Nunito,sans-serif">${done ? "✓" : n}</span>
    </div>`,
    iconSize: [30, 42],
    iconAnchor: [15, 36],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14, { animate: true });
    else if (points.length >= 2) map.fitBounds(points, { padding: [50, 50], maxZoom: 15, animate: true });
  }, [JSON.stringify(points)]); // eslint-disable-line
  return null;
}

export function Itinerary({ errand, isRunner, onChange }) {
  const stops = errand.stops || [];
  const geo = stops.filter((s) => s.lat != null && s.lng != null);
  const points = geo.map((s) => [s.lat, s.lng]);
  const doneCount = stops.filter((s) => s.done).length;
  const canToggle = isRunner && ["accepted", "in_progress"].includes(errand.status);

  const toggle = async (stop) => {
    try {
      const { data } = await api.post(`/errands/${errand.id}/stops/${stop.id}/toggle`, {});
      if (onChange) onChange(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Couldn't update stop");
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm" data-testid="itinerary-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-black text-lg flex items-center gap-2"><Route size={18} className="text-primary" /> Itinerary</h3>
        <span data-testid="itinerary-progress" className="text-sm font-bold text-[#2F8F6E]">{doneCount}/{stops.length} done</span>
      </div>

      <div className="w-full h-2 rounded-full bg-gray-100 mb-5 overflow-hidden">
        <div className="h-full bg-[#66C2A5] transition-all" style={{ width: `${stops.length ? (doneCount / stops.length) * 100 : 0}%` }} />
      </div>

      {geo.length > 0 && (
        <div className="mb-5 overflow-hidden rounded-2xl border border-gray-100">
          <MapContainer center={points[0]} zoom={13} scrollWheelZoom={false} style={{ height: "300px", width: "100%", zIndex: 0 }}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {geo.map((s, i) => (
              <Marker key={s.id} position={[s.lat, s.lng]} icon={numberIcon(stops.indexOf(s) + 1, s.done)} />
            ))}
            <FitBounds points={points} />
          </MapContainer>
        </div>
      )}

      <div className="space-y-2.5">
        {stops.map((s, i) => (
          <div key={s.id} data-testid={`itinerary-stop-${i}`}
            className={`flex items-start gap-3 rounded-2xl border p-3.5 transition-colors ${s.done ? "border-[#CDEBDF] bg-[#E7F6F0]" : "border-gray-200 bg-white"}`}>
            <button type="button" disabled={!canToggle} data-testid={`stop-toggle-${i}`} onClick={() => toggle(s)}
              className={`mt-0.5 w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${s.done ? "bg-[#2F8F6E] border-[#2F8F6E]" : "border-gray-300 bg-white"} ${canToggle ? "cursor-pointer hover:border-primary" : "cursor-default"}`}>
              {s.done ? <Check size={15} className="text-white" /> : <span className="text-xs font-black text-[#6B7280]">{i + 1}</span>}
            </button>
            <div className="min-w-0 flex-1">
              <p className={`font-bold text-sm ${s.done ? "line-through text-[#6B7280]" : "text-[#1A1D20]"}`}>{s.label}</p>
              {s.details ? <p className="text-xs text-[#6B7280] mt-0.5">{s.details}</p> : null}
              {(s.exact_location || s.location) ? (
                <p className="text-xs text-[#9CA3AF] mt-1 flex items-center gap-1"><MapPin size={11} /> {s.exact_location || s.location}</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {canToggle && (
        <p className="text-[11px] text-[#9CA3AF] mt-4 text-center">Tap each circle to check off a stop as you complete it.</p>
      )}
    </div>
  );
}
